import { useEffect } from "react";
import { SITE_URL, DEFAULT_OG_IMAGE, absoluteUrl } from "../../lib/site";

// Lightweight SEO helper — sets <title>, description, og/twitter meta, and
// canonical imperatively. React 19 hoists <title> from JSX, but managing meta
// attributes is simpler from a useEffect (avoids adding react-helmet).
//
// Props:
// - title       — page <title> + og:title + twitter:title
// - description — meta description + og:description + twitter:description
// - canonical   — path like "/contact" (absolutized) or absolute URL
// - image       — absolute image URL (falls back to DEFAULT_OG_IMAGE)
// - noindex     — when true, emits <meta name="robots" content="noindex,nofollow">
//                 (auto-true on the preview environment hostname)
export const SEO = ({ title, description, canonical, image, noindex }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (content == null || content === "") {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const ogImage = image || DEFAULT_OG_IMAGE;
    const canonicalUrl = canonical ? absoluteUrl(canonical) : `${SITE_URL}${typeof window !== "undefined" ? window.location.pathname : "/"}`;

    setMeta("description", description);
    setMeta("og:site_name", "Built Different Hockey", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Robots: explicit noindex when requested, OR auto when running on a
    // preview host so search engines don't index dev URLs.
    const isPreview =
      typeof window !== "undefined" &&
      /(?:preview\.emergentagent\.com|localhost|127\.0\.0\.1)/.test(window.location.hostname);
    if (noindex || isPreview) {
      setMeta("robots", "noindex,nofollow");
    } else {
      const existing = document.head.querySelector('meta[name="robots"]');
      if (existing) existing.remove();
    }

    // Canonical link — always absolute, always pointing at production.
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);
  }, [title, description, canonical, image, noindex]);

  return null;
};
